import '../../i18n/autoTranslate';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import PhoneInterview from '../../../pages/PhoneInterview';

const InterviewVideoInterface = ({ 
  isRecording = false,
  onToggleRecording,
  sessionMode = 'video',
  aiInterviewer = null
}) => {
  const [isUserCameraOn, setIsUserCameraOn] = useState(true);
  const [isUserMicOn, setIsUserMicOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userStream, setUserStream] = useState(null);
  const userVideoRef = useRef(null);
  const aiVideoRef = useRef(null);

  const mockAiInterviewer = {
    name: "Sarah Chen",
    role: "Senior Technical Interviewer",
    company: "TechCorp",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
    personality: "Professional, encouraging, detail-oriented"
  };

  const currentInterviewer = aiInterviewer || mockAiInterviewer;

  // Function to get user media (camera and microphone)
  const getUserMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: sessionMode === 'video', audio: true });
      setUserStream(stream);
      if (userVideoRef.current) {
        userVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing user media:', error);
    }
  };

  // Function to stop user media tracks
  const stopUserMedia = () => {
    if (userStream) {
      userStream.getTracks().forEach(track => track.stop());
      setUserStream(null);
      if (userVideoRef.current) {
        userVideoRef.current.srcObject = null;
      }
    }
  };

  useEffect(() => {
    getUserMedia();

    return () => {
      stopUserMedia();
    };
  }, [sessionMode]);

  useEffect(() => {
    if (userStream) {
      userStream.getVideoTracks().forEach(track => track.enabled = isUserCameraOn);
    }
  }, [isUserCameraOn, userStream]);

  useEffect(() => {
    if (userStream) {
      userStream.getAudioTracks().forEach(track => track.enabled = isUserMicOn);
    }
  }, [isUserMicOn, userStream]);

  // Fullscreen effect
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleUserCamera = () => {
    setIsUserCameraOn(!isUserCameraOn);
  };

  const toggleUserMic = () => {
    setIsUserMicOn(!isUserMicOn);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-gray-900 to-black rounded-lg overflow-hidden">

      {/* AI Interviewer Video Feed */}
      <div className="relative w-full h-full">
        
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-indigo-500/30 shadow-2xl">
                <Image
                  src={currentInterviewer?.avatar}
                  alt={currentInterviewer?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-semibold text-white mb-1">{currentInterviewer?.name}</h3>
              <p className="text-indigo-400 text-sm mb-1">{currentInterviewer?.role}</p>
              <p className="text-gray-400 text-xs">{currentInterviewer?.company}</p>
              
              {/* AI Speaking Indicator */}
              <div className="flex items-center justify-center mt-4">
                <div className="w-16 h-2 bg-indigo-500/30 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-400 animate-pulse" style={{ width: '100%' }}></div>
                </div>
              </div>
            </div>
          </div>
        

        {/* Recording Indicator */}
        {isRecording && (
          <div className="absolute top-4 left-4 flex items-center space-x-2 bg-red-500/90 text-white px-3 py-1 rounded-full text-sm backdrop-blur">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <span>Recording</span>
          </div>
        )}

        {/* Session Info */}
        <div style={{zIndex:99999}} className="absolute top-4 right-4 bg-black/50 backdrop-blur text-white px-3 py-1 rounded-lg text-sm border border-gray-600">
          <span className="capitalize">{sessionMode} Interview</span>
        </div>

        {/* Fullscreen Toggle */}
        <button
          style={{zIndex:99999}}
          onClick={toggleFullscreen}
          className="absolute bottom-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-lg flex items-center justify-center transition-colors duration-200 border border-gray-600"
        >
          <Icon name={isFullscreen ? "Minimize2" : "Maximize2"} size={20} />
        </button>
      </div>
      
      {/* User Video Feed (Picture-in-Picture) */}
      
        <div className="absolute bottom-4 left-4 w-32 h-24 md:w-40 md:h-30 bg-gray-800 rounded-lg overflow-hidden border-2 border-indigo-500/30">
          {isUserCameraOn && userStream ? (
            <video ref={userVideoRef} autoPlay playsInline muted className="w-full h-full object-cover"></video>
          ) : (!isUserCameraOn && !userStream) ? (
            <div className="w-full h-full bg-gray-900 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <span className="text-xs text-gray-400">You</span>
              </div>
            </div>
          ) : (
            <div className="w-full h-full bg-gray-900 flex items-center justify-center">
              <Icon name="CameraOff" size={20} className="text-gray-500" />
            </div>
          )}

          {/* User Controls Overlay */}
          <div className="absolute bottom-1 left-1 right-1 flex justify-center space-x-1">
            <button
              onClick={toggleUserCamera}
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors duration-200 border border-gray-600 ${
                isUserCameraOn 
                  ? 'bg-white/20 text-white hover:bg-white/30' 
                  : 'bg-red-500/80 text-white hover:bg-red-600'
              }`}
            >
              <Icon name={isUserCameraOn ? "Video" : "VideoOff"} size={12} />
            </button>
            <button
              onClick={toggleUserMic}
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors duration-200 border border-gray-600 ${
                isUserMicOn 
                  ? 'bg-white/20 text-white hover:bg-white/30' 
                  : 'bg-red-500/80 text-white hover:bg-red-600'
              }`}
            >
              <Icon name={isUserMicOn ? "Mic" : "MicOff"} size={12} />
            </button>
          </div>
        </div>
      
      
      {/* Audio Mode Waveform */}
      
        <div className="absolute bottom-4 left-4 right-4 h-16 bg-black/50 backdrop-blur rounded-lg flex items-center justify-center border border-gray-600">
          <div className="flex items-center space-x-1">
            {[...Array(20)]?.map((_, i) => (
              <div
                key={i}
                className="w-1 bg-indigo-400 rounded-full animate-pulse"
                style={{
                  height: `${Math.random() * 32 + 8}px`,
                  animationDelay: `${i * 50}ms`
                }}
              />
            ))}
          </div>
        </div>
      
    </div>
  );
};

export default InterviewVideoInterface;
